import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  GET_SUBSCRIPTION_PACKAGES,
  GET_USER_SUBSCRIPTIONS,
  GET_ALL_USER_SUBSCRIPTIONS,
  CREATE_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION,
  CALCULATE_PACKAGE_PRICING
} from '@shared/graphql/queries/subscriptionPackages';
import { useAuth } from '../contexts/AuthContext';

export interface UseSubscriptionPackagesReturn {
  packages: any[];
  userSubscriptions: any[];
  loading: boolean;
  error: string | null;
  selectedPackages: string[];
  billingCycle: 'WEEKLY' | 'MONTHLY';
  totalPrice: number;
  totalSavings: number;
  setBillingCycle: (cycle: 'WEEKLY' | 'MONTHLY') => void;
  togglePackageSelection: (packageId: string) => void;
  subscribeToPackages: () => void;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  isPackageSubscribed: (packageId: string) => boolean;
  refetchSubscriptions: () => void;
  setSelectedPackages: (packages: string[]) => void;
  hasMultiPackageDiscount: boolean;
  getDiscountMessage: () => string | null;
  isPaymentModalOpen: boolean;
  setIsPaymentModalOpen: (open: boolean) => void;
  handlePaymentSuccess: () => void;
  showEverythingNotification: boolean;
  setShowEverythingNotification: (show: boolean) => void;
  getEverythingPackagePrice: () => number;
}

export const useSubscriptionPackages = (meetingDate?: string): UseSubscriptionPackagesReturn => {
  const { user } = useAuth();
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [billingCycle, setBillingCycle] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
  const [error, setError] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);
  const [showEverythingNotification, setShowEverythingNotification] = useState<boolean>(false);

  // Fetch subscription packages
  const {
    data: packagesData,
    loading: packagesLoading,
    error: packagesError
  } = useQuery(GET_SUBSCRIPTION_PACKAGES);

  // Fetch user subscriptions - use date-aware query if meetingDate is provided
  const {
    data: subscriptionsData,
    loading: subscriptionsLoading,
    error: subscriptionsError,
    refetch: refetchSubscriptions
  } = useQuery(meetingDate ? GET_ALL_USER_SUBSCRIPTIONS : GET_USER_SUBSCRIPTIONS, {
    variables: meetingDate ? { meetingDate } : undefined,
    skip: !user
  });

  // Get multi-package pricing
  const {
    data: pricingData,
    loading: pricingLoading
  } = useQuery(CALCULATE_PACKAGE_PRICING, {
    variables: {
      packageIds: selectedPackages.map(id => parseInt(id)),
      billingCycle
    },
    skip: selectedPackages.length === 0,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false
  });

  // Remove the old CREATE_SUBSCRIPTION mutation - using PaymentModal instead

  // Cancel subscription mutation
  const [cancelSubscriptionMutation] = useMutation(CANCEL_SUBSCRIPTION);

  // Calculate loading state - exclude pricing loading to prevent flickering
  const loading = packagesLoading || subscriptionsLoading;

  // Handle errors
  useEffect(() => {
    if (packagesError || subscriptionsError) {
      setError(packagesError?.message || subscriptionsError?.message || 'An error occurred');
    } else {
      setError(null);
    }
  }, [packagesError, subscriptionsError]);

  // Get packages and subscriptions
  const packages = packagesData?.getAvailablePackages || [];
  const userSubscriptions = subscriptionsData?.getCurrentUserSubscription?.activeSubscriptions || [];

  // Calculate pricing
  const pricingResult = pricingData?.calculateMultiPackagePrice;
  const totalPrice = billingCycle === 'WEEKLY' ?
    pricingResult?.totalWeeklyPrice || 0 :
    pricingResult?.totalMonthlyPrice || 0;
  const totalSavings = pricingResult?.monthlySavings || 0;

  // Toggle package selection with "Everything" exclusive logic and smart auto-selection
  const togglePackageSelection = (packageId: string) => {
    setSelectedPackages(prev => {
      const selectedPackage = packages.find((pkg: any) => pkg.id === packageId);
      const isEverythingPackage = selectedPackage?.packageCode === 'everything';
      
      if (prev.includes(packageId)) {
        // Deselecting a package
        return prev.filter(id => id !== packageId);
      } else {
        // Selecting a package
        let newSelection: string[];
        
        if (isEverythingPackage) {
          // If selecting "Everything", deselect all others
          newSelection = [packageId];
        } else {
          // If selecting any other package, remove "Everything" if it's selected
          const everythingPackage = packages.find((pkg: any) => pkg.packageCode === 'everything');
          const filteredPrev = everythingPackage ? prev.filter(id => id !== everythingPackage.id) : prev;
          newSelection = [...filteredPrev, packageId];
        }
        
        // Check if combination costs more than "Everything" and show notification
        if (!isEverythingPackage && newSelection.length > 1) {
          const everythingPackage = packages.find((pkg: any) => pkg.packageCode === 'everything');
          if (everythingPackage) {
            // Calculate current selection cost vs Everything package cost
            const currentCost = calculateSelectionCost(newSelection);
            const everythingCost = billingCycle === 'WEEKLY' ?
              everythingPackage.priceWeekly : everythingPackage.priceMonthly;
            
            if (currentCost >= everythingCost) {
              // Show notification instead of auto-switching
              setShowEverythingNotification(true);
            }
          }
        }
        
        return newSelection;
      }
    });
  };

  // Helper function to calculate cost of current selection
  // Now uses the GraphQL pricing query instead of hardcoded discount logic
  const calculateSelectionCost = (selection: string[]): number => {
    if (selection.length === 0) return 0;
    
    // Use the pricing result from GraphQL query if available
    if (pricingResult && selection.length === selectedPackages.length) {
      return billingCycle === 'WEEKLY' ?
        pricingResult.totalWeeklyPrice || 0 :
        pricingResult.totalMonthlyPrice || 0;
    }
    
    // Fallback: calculate based on individual package prices (no discounts applied here)
    // The actual discounting is handled by the backend pricing system
    const selectedPkgs = packages.filter((pkg: any) => selection.includes(pkg.id));
    return selectedPkgs.reduce((total: number, pkg: any) => {
      return total + (billingCycle === 'WEEKLY' ? pkg.priceWeekly : pkg.priceMonthly);
    }, 0);
  };

  // Check if package is subscribed - memoized to prevent unnecessary re-renders
  const isPackageSubscribed = useCallback((packageId: string): boolean => {
    return userSubscriptions.some((sub: any) =>
      sub.package?.id === packageId && sub.isActive
    );
  }, [userSubscriptions]);

  // Subscribe to selected packages - opens PaymentModal instead of direct subscription
  const subscribeToPackages = () => {
    if (!user || selectedPackages.length === 0) {
      setError('Please select at least one package');
      return;
    }

    setError(null);
    setIsPaymentModalOpen(true);
  };

  // Handle successful payment
  const handlePaymentSuccess = async () => {
    setSelectedPackages([]);
    setIsPaymentModalOpen(false);
    await refetchSubscriptions();
  };

  // Cancel subscription
  const cancelSubscription = async (subscriptionId: string) => {
    try {
      setError(null);
      
      await cancelSubscriptionMutation({
        variables: {
          id: subscriptionId
        }
      });

      await refetchSubscriptions();
      
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    }
  };

  // Check if user has multi-package discount
  const hasMultiPackageDiscount = selectedPackages.length > 1;

  // Get discount message - now uses dynamic pricing from backend
  const getDiscountMessage = (): string | null => {
    if (selectedPackages.length <= 1) return null;
    
    const everythingPackage = packages.find((pkg: any) => pkg.packageCode === 'everything');
    const isEverythingSelected = everythingPackage && selectedPackages.includes(everythingPackage.id);
    
    if (isEverythingSelected) {
      return "Everything package includes all tracks - maximum value!";
    }
    
    if (selectedPackages.length > 1 && pricingResult) {
      // Calculate savings from multi-package pricing
      const individualTotal = packages
        .filter((pkg: any) => selectedPackages.includes(pkg.id))
        .reduce((total: number, pkg: any) => {
          return total + (billingCycle === 'WEEKLY' ? pkg.priceWeekly : pkg.priceMonthly);
        }, 0);
      
      const discountedTotal = billingCycle === 'WEEKLY' ?
        pricingResult.totalWeeklyPrice :
        pricingResult.totalMonthlyPrice;
      
      const savings = individualTotal - discountedTotal;
      
      if (savings > 0) {
        return `Multi-package discount: Save $${savings.toFixed(2)} with this combination!`;
      } else {
        return "Multi-package pricing applied!";
      }
    }
    
    return null;
  };

  // Get everything package price for notifications
  const getEverythingPackagePrice = (): number => {
    const everythingPackage = packages.find((pkg: any) => pkg.packageCode === 'everything');
    if (!everythingPackage) return 0;
    return billingCycle === 'WEEKLY' ? everythingPackage.priceWeekly : everythingPackage.priceMonthly;
  };

  return {
    packages,
    userSubscriptions,
    loading,
    error,
    selectedPackages,
    billingCycle,
    totalPrice,
    totalSavings,
    setBillingCycle,
    togglePackageSelection,
    subscribeToPackages,
    cancelSubscription,
    isPackageSubscribed,
    refetchSubscriptions,
    setSelectedPackages,
    hasMultiPackageDiscount,
    getDiscountMessage,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    handlePaymentSuccess,
    showEverythingNotification,
    setShowEverythingNotification,
    getEverythingPackagePrice
  };
};